import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import Layout from "../../components/Layout";
import { useAuth } from "../../context/AuthContext";
import {
    ChartBar, Users, Buildings, ArrowClockwise, ShieldCheck, Flask,
    Broadcast, MapTrifold, TrendUp, ArrowRight, MagnifyingGlass, Key, Database, CreditCard, Warning,
} from "@phosphor-icons/react";

const PLAN_BADGE = {
    free: "bg-gray-100 text-gray-700",
    pro: "bg-blue-100 text-blue-800",
    business: "bg-purple-100 text-purple-800",
    enterprise: "bg-emerald-100 text-emerald-800",
};

const STATUS_BADGE = {
    active: "bg-emerald-100 text-emerald-800",
    suspended: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
};

export default function AdminHome() {
    const { user } = useAuth();

    if (user && user.role !== "admin") {
        return (
            <Layout>
                <div data-testid="admin-forbidden" className="p-8 text-center">
                    <ShieldCheck size={32} weight="bold" className="mx-auto text-rose-600 mb-3" />
                    <div className="font-display-black text-2xl mb-2">Accesso negato</div>
                    <div className="text-sm text-gray-600">
                        Questa sezione è riservata agli amministratori.
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="admin-home">
                <header>
                    <div className="overline text-gray-500 mb-1">Area amministratore</div>
                    <h1 className="font-display-black text-3xl sm:text-4xl tracking-tight">
                        Pannello Admin
                    </h1>
                    <p className="text-sm text-gray-600 mt-1.5 max-w-2xl">
                        Aree tecniche e strumenti operativi. Statistiche, abbonamenti,
                        account e organizzazioni sono nella sezione <b>Utenti</b> del menu.
                    </p>
                </header>

                <QuickActions />

                <DangerZone />
            </div>
        </Layout>
    );
}


function DangerZone() {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (text !== "AZZERA") { toast.error('Scrivi esattamente "AZZERA" per confermare'); return; }
        if (!window.confirm("Confermi l'azzeramento definitivo di utenti test, analisi di sviluppo e contatori? L'operazione NON è reversibile.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/panel/reset-test-data", { confirmation_text: text });
            setResult(data);
            setText("");
            toast.success("Dati di test azzerati — statistiche ripartite da zero");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setBusy(false); }
    };

    return (
        <section className="border border-rose-300 rounded-xl bg-white p-5" data-testid="admin-danger-zone">
            <h2 className="font-display-black text-lg text-rose-700 flex items-center gap-2">
                <Warning size={18} weight="fill" /> Zona pericolosa — Azzera dati di test
            </h2>
            <p className="text-sm text-gray-600 mt-1.5 max-w-2xl">
                Cancella <b>tutti gli utenti e le organizzazioni di test</b> (resta solo l'admin), le <b>analisi
                e aste di sviluppo</b> dell'org admin e azzera i <b>contatori</b> (spesa AI, quote analisi).
                Vengono mantenuti: inventario nazionale, perizie acquisite, golden cases e statistiche di acquisizione.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-4">
                <input
                    data-testid="reset-confirm-input"
                    value={text}
                    onChange={(e) => setText(e.target.value.toUpperCase())}
                    placeholder='Scrivi "AZZERA"'
                    className="h-10 px-3 text-sm border border-gray-300 rounded-lg bg-white focus:border-rose-500 outline-none w-44"
                />
                <button
                    data-testid="reset-test-data-btn"
                    onClick={run}
                    disabled={busy || text !== "AZZERA"}
                    className="h-10 px-5 text-sm font-bold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 transition-colors"
                >
                    {busy ? "Azzeramento in corso…" : "Azzera dati di test"}
                </button>
            </div>
            {result && (
                <div className="mt-4 text-xs text-gray-600 border border-gray-200 rounded-lg p-3" data-testid="reset-result">
                    <div className="font-bold text-emerald-700 mb-1">✓ Azzeramento completato</div>
                    {Object.entries(result.deleted || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                        <div key={k}>• {k}: {v} eliminati</div>
                    ))}
                </div>
            )}
        </section>
    );
}


function QuickActions() {
    const [retrying, setRetrying] = useState(false);

    const retryFailed = async () => {
        setRetrying(true);
        try {
            const { data } = await api.post("/admin/panel/analyses/retry-failed");
            if (data.retried > 0) {
                toast.success(`${data.retried} analisi rimesse in coda`);
            } else {
                toast.info("Nessuna analisi fallita da riavviare");
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setRetrying(false);
        }
    };

    const links = [
        { to: "/admin/utenti", label: "Utenti / Abbonamenti", icon: CreditCard, highlight: true },
        { to: "/kpi", label: "Vai a KPI", icon: ChartBar },
        { to: "/validation", label: "Vai a Validation", icon: Flask },
        { to: "/radar", label: "Vai al Radar", icon: Broadcast },
        { to: "/admin/data-quality", label: "Data Quality", icon: Database },
        { to: "/admin/copertura", label: "Copertura", icon: MapTrifold },
        { to: "/admin/crescita", label: "Crescita", icon: TrendUp },
        { to: "/admin/richieste", label: "Richieste GDPR", icon: ShieldCheck },
    ];

    return (
        <section
            data-testid="admin-quick-actions"
            className="border border-gray-200 bg-white p-5"
        >
            <div className="overline text-gray-500 mb-3">Azioni rapide</div>
            <div className="flex flex-wrap gap-2">
                {links.map((l) => {
                    const Icon = l.icon;
                    return (
                        <Link
                            key={l.to}
                            to={l.to}
                            data-testid={`admin-quick-${l.to.replace(/[^a-z0-9]/gi, "-")}`}
                            className={`h-10 px-3 flex items-center gap-2 border text-xs font-semibold transition-colors ${l.highlight ? "border-black bg-black text-white hover:bg-emerald-600 hover:border-emerald-600" : "border-gray-300 bg-white hover:border-black"}`}
                        >
                            <Icon size={14} weight="bold" />
                            {l.label}
                            <ArrowRight size={12} weight="bold" className={l.highlight ? "text-white/60" : "text-gray-400"} />
                        </Link>
                    );
                })}
                <button
                    onClick={retryFailed}
                    disabled={retrying}
                    data-testid="admin-quick-retry-failed"
                    className="h-10 px-3 flex items-center gap-2 border border-rose-300 bg-rose-50 text-rose-800 text-xs font-semibold hover:border-rose-700 transition-colors disabled:opacity-50"
                >
                    <ArrowClockwise size={14} weight="bold" />
                    {retrying ? "Avvio…" : "Riavvia analisi fallite"}
                </button>
            </div>
        </section>
    );
}


export function StatsPane() {
    const [stats, setStats] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get("/admin/panel/stats")
            .then((r) => setStats(r.data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    }, []);

    if (err) return <div data-testid="admin-stats-error" className="text-rose-700 text-sm">{err}</div>;
    if (!stats) return <div className="text-sm text-gray-500 animate-pulse">Caricamento…</div>;

    const byPlan = stats.users.by_plan || {};
    const cards = [
        { id: "analyses-total", label: "Analisi totali", value: stats.analyses.total, kind: "primary" },
        { id: "analyses-completed", label: "Completate", value: stats.analyses.completed, kind: "success" },
        { id: "analyses-failed", label: "Fallite", value: stats.analyses.failed, kind: "danger" },
        { id: "analyses-running", label: "In esecuzione", value: stats.analyses.running, kind: "info" },
        { id: "users-total", label: "Utenti registrati", value: stats.users.total, sub: `${stats.users.active} attivi` },
        { id: "orgs-total", label: "Organizzazioni", value: stats.organizations.total, sub: `${stats.organizations.active} attive` },
        { id: "radar-total", label: "Aste nel Radar", value: stats.radar.total, sub: `${stats.radar.promoted} promosse` },
        { id: "auctions-total", label: "Aste profonde", value: stats.auctions.total },
    ];

    const cardStyle = (kind) => {
        switch (kind) {
            case "success": return "border-emerald-200 bg-emerald-50";
            case "danger":  return "border-rose-200 bg-rose-50";
            case "info":    return "border-blue-200 bg-blue-50";
            case "primary": return "border-gray-300 bg-white";
            default:        return "border-gray-200 bg-white";
        }
    };

    return (
        <div data-testid="admin-stats-pane" className="space-y-3">
            {/* Card riepilogo utenti */}
            <div data-testid="admin-users-summary-card"
                 className="border-2 border-black bg-white p-4 flex flex-wrap items-center gap-x-8 gap-y-3">
                <div>
                    <div className="overline text-gray-500 mb-1">Utenti totali</div>
                    <div className="font-display-black text-3xl tracking-tight" data-testid="users-summary-total">
                        {stats.users.total.toLocaleString("it-IT")}
                    </div>
                </div>
                <div>
                    <div className="overline text-gray-500 mb-1">Nuovi questa settimana</div>
                    <div className="font-display-black text-3xl tracking-tight text-emerald-700" data-testid="users-summary-new-week">
                        +{(stats.users.new_this_week ?? 0).toLocaleString("it-IT")}
                    </div>
                </div>
                <div className="flex-1 min-w-[260px]">
                    <div className="overline text-gray-500 mb-2">Per piano</div>
                    <div className="flex flex-wrap gap-2" data-testid="users-summary-by-plan">
                        {[["free", "Free", "bg-gray-100 text-gray-700"],
                          ["standard", "Standard", "bg-blue-100 text-blue-800"],
                          ["pro", "Pro", "bg-purple-100 text-purple-800"],
                          ["enterprise", "Admin", "bg-emerald-100 text-emerald-800"]].map(([k, label, cls]) => (
                            <span key={k} data-testid={`users-plan-${k}`}
                                  className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${cls}`}>
                                {label} <span className="font-mono-tight">{(byPlan[k] ?? 0).toLocaleString("it-IT")}</span>
                            </span>
                        ))}
                    </div>
                </div>
                <Link to="/admin/utenti" data-testid="users-summary-manage-link"
                      className="h-9 px-4 inline-flex items-center gap-2 bg-black text-white text-xs font-semibold hover:bg-emerald-600 transition-colors">
                    Gestisci <ArrowRight size={12} weight="bold" />
                </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cards.map((c) => (
                <div
                    key={c.id}
                    data-testid={`admin-stat-${c.id}`}
                    className={`p-4 border ${cardStyle(c.kind)}`}
                >
                    <div className="overline text-gray-500 mb-1">{c.label}</div>
                    <div className="font-display-black text-3xl tracking-tight">{c.value.toLocaleString("it-IT")}</div>
                    {c.sub && <div className="text-[11px] text-gray-500 mt-1">{c.sub}</div>}
                </div>
            ))}
            </div>
        </div>
    );
}


export function UsersPane({ meId }) {
    const [q, setQ] = useState("");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resetResult, setResetResult] = useState(null); // {email, temp_password}

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            params.set("limit", "200");
            const { data } = await api.get(`/admin/panel/users?${params.toString()}`);
            setUsers(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const toggleActive = async (u) => {
        try {
            const { data } = await api.patch(`/admin/panel/users/${u.id}`, { is_active: !u.is_active });
            setUsers((arr) => arr.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
            toast.success(data.is_active ? "Utente attivato" : "Utente disattivato");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const resetPassword = async (u) => {
        if (!window.confirm(`Generare una nuova password temporanea per ${u.email}?`)) return;
        try {
            const { data } = await api.patch(`/admin/panel/users/${u.id}`, { reset_password: true });
            setResetResult({ email: u.email, temp_password: data.temp_password });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-users-pane" className="space-y-4">
            <div className="flex items-center gap-2">
                <div className="flex-1 relative max-w-md">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        data-testid="admin-users-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Cerca per email o nome…"
                        className="h-10 w-full pl-9 pr-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                    />
                </div>
                <button
                    onClick={load}
                    data-testid="admin-users-search-btn"
                    className="h-10 px-4 bg-black text-white text-xs font-semibold hover:bg-gray-800"
                >
                    Cerca
                </button>
            </div>

            {resetResult && (
                <div className="border border-amber-300 bg-amber-50 p-4 text-sm" data-testid="admin-users-reset-result">
                    <div className="flex items-start gap-2">
                        <Key size={16} weight="bold" className="text-amber-700 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-bold text-amber-900">Password temporanea generata</div>
                            <div className="mt-1">
                                Utente <b>{resetResult.email}</b>: nuova password{" "}
                                <code className="bg-white px-2 py-0.5 border border-amber-300 select-all" data-testid="admin-users-temp-password">
                                    {resetResult.temp_password}
                                </code>
                            </div>
                            <div className="text-xs text-amber-800 mt-1">
                                Comunicala all&apos;utente in modo sicuro. Non sarà più visibile dopo aver chiuso questo riquadro.
                            </div>
                            <button
                                onClick={() => setResetResult(null)}
                                data-testid="admin-users-reset-dismiss"
                                className="mt-2 text-xs underline text-amber-900"
                            >
                                Ho capito, nascondi
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="admin-users-table">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500 animate-pulse">Caricamento utenti…</div>
                ) : users.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500">Nessun utente trovato.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2 font-semibold">Email</th>
                                <th className="px-3 py-2 font-semibold">Nome</th>
                                <th className="px-3 py-2 font-semibold">Ruolo</th>
                                <th className="px-3 py-2 font-semibold">Organizzazione</th>
                                <th className="px-3 py-2 font-semibold">Stato</th>
                                <th className="px-3 py-2 font-semibold text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr
                                    key={u.id}
                                    data-testid={`admin-users-row-${u.id}`}
                                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                                >
                                    <td className="px-3 py-2 font-mono-tight text-xs">{u.email}</td>
                                    <td className="px-3 py-2">{u.full_name}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-xs uppercase tracking-wider text-gray-600">{u.role}</span>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-gray-700">{u.organization_name || "—"}</td>
                                    <td className="px-3 py-2">
                                        <span
                                            data-testid={`admin-users-status-${u.id}`}
                                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${
                                                u.is_active
                                                    ? "bg-emerald-100 text-emerald-800"
                                                    : "bg-rose-100 text-rose-800"
                                            }`}
                                        >
                                            {u.is_active ? "Attivo" : "Disattivo"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-2">
                                        <button
                                            onClick={() => toggleActive(u)}
                                            disabled={u.id === meId}
                                            data-testid={`admin-users-toggle-${u.id}`}
                                            className="text-xs px-2 py-1 border border-gray-300 hover:border-black disabled:opacity-40 disabled:cursor-not-allowed"
                                            title={u.id === meId ? "Non puoi disattivare te stesso" : undefined}
                                        >
                                            {u.is_active ? "Disattiva" : "Attiva"}
                                        </button>
                                        <button
                                            onClick={() => resetPassword(u)}
                                            data-testid={`admin-users-reset-${u.id}`}
                                            className="text-xs px-2 py-1 border border-amber-300 bg-amber-50 hover:border-amber-700 text-amber-800"
                                        >
                                            Reset password
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="text-xs text-gray-500">{users.length} utenti</div>
        </div>
    );
}


export function OrgsPane({ meOrgId }) {
    const [q, setQ] = useState("");
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            params.set("limit", "200");
            const { data } = await api.get(`/admin/panel/organizations?${params.toString()}`);
            setOrgs(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const patch = async (o, body) => {
        try {
            const { data } = await api.patch(`/admin/panel/organizations/${o.id}`, body);
            setOrgs((arr) => arr.map((x) => (x.id === data.id ? { ...x, ...data } : x)));
            toast.success("Organizzazione aggiornata");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-orgs-pane" className="space-y-4">
            <div className="flex items-center gap-2">
                <div className="flex-1 relative max-w-md">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        data-testid="admin-orgs-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Cerca per nome organizzazione…"
                        className="h-10 w-full pl-9 pr-3 border border-gray-300 text-sm focus:border-black focus:outline-none"
                    />
                </div>
                <button
                    onClick={load}
                    data-testid="admin-orgs-search-btn"
                    className="h-10 px-4 bg-black text-white text-xs font-semibold hover:bg-gray-800"
                >
                    Cerca
                </button>
            </div>

            <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="admin-orgs-table">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500 animate-pulse">Caricamento…</div>
                ) : orgs.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500">Nessuna organizzazione trovata.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2 font-semibold">Nome</th>
                                <th className="px-3 py-2 font-semibold">Piano</th>
                                <th className="px-3 py-2 font-semibold">Sub.</th>
                                <th className="px-3 py-2 font-semibold">Utenti</th>
                                <th className="px-3 py-2 font-semibold">Analisi</th>
                                <th className="px-3 py-2 font-semibold">Uso mese</th>
                                <th className="px-3 py-2 font-semibold">Stato</th>
                                <th className="px-3 py-2 font-semibold text-right">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orgs.map((o) => (
                                <tr
                                    key={o.id}
                                    data-testid={`admin-orgs-row-${o.id}`}
                                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                                >
                                    <td className="px-3 py-2">
                                        <div className="font-semibold flex items-center gap-1.5">
                                            {o.name}
                                            {o.plan !== "enterprise" && (
                                                <span data-testid={`admin-orgs-type-${o.id}`}
                                                      className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 font-bold ${o.organization_type === "company" ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-600"}`}>
                                                    {o.organization_type === "company" ? "Azienda" : "Privato"}
                                                </span>
                                            )}
                                        </div>
                                        {o.organization_type === "company" && o.billing && (
                                            <div className="text-[11px] text-gray-500 font-mono-tight">
                                                P.IVA {o.billing.vat_number} · {o.billing.city} ({o.billing.province}) · {o.billing.sdi_code ? `SDI ${o.billing.sdi_code}` : `PEC ${o.billing.pec}`}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <select
                                            value={o.plan}
                                            onChange={(e) => patch(o, { plan: e.target.value })}
                                            data-testid={`admin-orgs-plan-${o.id}`}
                                            className={`text-[10px] uppercase tracking-wider px-2 py-1 border border-transparent ${PLAN_BADGE[o.plan] || PLAN_BADGE.free} cursor-pointer hover:border-gray-400`}
                                        >
                                            {Object.keys(PLAN_BADGE).map((p) => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2">
                                        <select
                                            value={o.subscription_status}
                                            onChange={(e) => patch(o, { subscription_status: e.target.value })}
                                            data-testid={`admin-orgs-subscription-${o.id}`}
                                            className={`text-[10px] uppercase tracking-wider px-2 py-1 border border-transparent ${STATUS_BADGE[o.subscription_status] || STATUS_BADGE.active} cursor-pointer hover:border-gray-400`}
                                        >
                                            {Object.keys(STATUS_BADGE).map((s) => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-3 py-2 font-mono-tight">{o.users_count}</td>
                                    <td className="px-3 py-2 font-mono-tight">{o.analyses_count}</td>
                                    <td className="px-3 py-2 font-mono-tight text-xs">
                                        {o.analyses_used_this_month} / {o.monthly_analysis_limit}
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            data-testid={`admin-orgs-status-${o.id}`}
                                            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${
                                                o.is_active
                                                    ? "bg-emerald-100 text-emerald-800"
                                                    : "bg-rose-100 text-rose-800"
                                            }`}
                                        >
                                            {o.is_active ? "Attiva" : "Sospesa"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <button
                                            onClick={() => patch(o, { is_active: !o.is_active })}
                                            disabled={o.id === meOrgId}
                                            data-testid={`admin-orgs-toggle-${o.id}`}
                                            className="text-xs px-2 py-1 border border-gray-300 hover:border-black disabled:opacity-40 disabled:cursor-not-allowed"
                                            title={o.id === meOrgId ? "Non puoi sospendere la tua stessa organizzazione" : undefined}
                                        >
                                            {o.is_active ? "Sospendi" : "Riattiva"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className="text-xs text-gray-500">{orgs.length} organizzazioni</div>
        </div>
    );
}
